import {useEffect, useRef} from 'react';

import {getDeviceType} from '@/shared/lib/env';

type LockId = symbol;

type BodyStyles = {
  overflowY: string;
  position: string;
  top: string;
  width: string;
};

const bodyState = {
  lockIds: new Set<LockId>(),
  scrollY: 0,
  shouldRestoreScrollPosition: false,
  prevStyles: {
    overflowY: '',
    position: '',
    top: '',
    width: '',
  },
};

function getBodyStyles(): BodyStyles {
  const {style} = document.body;

  return {
    overflowY: style.overflowY,
    position: style.position,
    top: style.top,
    width: style.width,
  };
}

function setBodyStyles(styles: BodyStyles): void {
  const {style} = document.body;
  style.overflowY = styles.overflowY;
  style.position = styles.position;
  style.top = styles.top;
  style.width = styles.width;
}

function releaseBodyScrollLock(lockId: LockId): void {
  if (!bodyState.lockIds.delete(lockId) || bodyState.lockIds.size > 0) {
    return;
  }

  setBodyStyles(bodyState.prevStyles);

  if (bodyState.shouldRestoreScrollPosition) {
    window.scrollTo(0, bodyState.scrollY);
    bodyState.shouldRestoreScrollPosition = false;
  }
}

function acquireBodyScrollLock(lockId: LockId): void {
  if (bodyState.lockIds.has(lockId)) {
    return;
  }

  if (bodyState.lockIds.size === 0) {
    bodyState.prevStyles = getBodyStyles();
    bodyState.scrollY = window.scrollY;

    const shouldUseFixedBodyLock = getDeviceType() === 'iOS';
    bodyState.shouldRestoreScrollPosition = shouldUseFixedBodyLock;

    if (shouldUseFixedBodyLock) {
      setBodyStyles({
        overflowY: 'hidden',
        position: 'fixed',
        top: `-${bodyState.scrollY}px`,
        width: '100%',
      });
    } else {
      setBodyStyles({
        ...bodyState.prevStyles,
        overflowY: 'hidden',
      });
    }
  }

  bodyState.lockIds.add(lockId);
}

export function useBodyScrollLock(isEnabled: boolean = true): void {
  const lockIdRef = useRef<LockId | null>(null);

  if (lockIdRef.current === null) {
    lockIdRef.current = Symbol('body-scroll-lock');
  }

  const lockId = lockIdRef.current;

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    acquireBodyScrollLock(lockId);

    return () => {
      releaseBodyScrollLock(lockId);
    };
  }, [isEnabled, lockId]);
}
